import { Provider, ProviderId, ProviderResponse, Regime } from './types';
import { supabase } from '@/integrations/supabase/client';

const createOpenAIProvider = (): Provider => ({
  id: 'openai',
  label: 'OpenAI',
  enabled: true,
  run: async (query: string, country?: string, regime: Regime = 'r2'): Promise<ProviderResponse> => {
    try {
      // Map UI regime to edge-function regime: r2 = current optimized default, r1 = full replication
      const efRegime = regime === 'r1' ? 'r1' : 'default';
      const { data, error } = await supabase.functions.invoke('analyze-sources', {
        body: { query, country: country === 'auto' ? undefined : country, regime: efRegime, debug: regime === 'r1' }
      });

      if (error) {
        console.error('OpenAI edge function error:', error);
        const errorContext = (error as any).context;
        if (errorContext) {
          try {
            const errorBody = await errorContext.json();
            console.error('OpenAI error body:', errorBody);
            throw new Error(`OpenAI: ${errorBody.error || error.message}`);
          } catch (parseErr) {
            console.error('Failed to parse error:', parseErr);
            throw new Error(`OpenAI: ${error.message}`);
          }
        }
        throw new Error(`OpenAI: ${error.message || 'API failed'}`);
      }

      if (data?.raw) {
        console.log(`[analyze-sources raw] regime=${efRegime} query="${query}"`, data.raw);
      }

      return {
        used_urls: data?.used_urls || [],
        considered: data?.considered || [],
        raw: data?.raw,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (!message.startsWith('OpenAI:')) {
        throw new Error(`OpenAI: ${message}`);
      }
      throw err;
    }
  },
});

const createClaudeProvider = (): Provider => ({
  id: 'claude',
  label: 'Claude',
  enabled: true,
  run: async (query: string, country?: string, _regime?: Regime): Promise<ProviderResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('run-batch-claude', {
        body: { query, country: country === 'auto' ? undefined : country }
      });

      if (error) {
        console.error('Claude edge function error:', error);
        const errorContext = (error as any).context;
        if (errorContext) {
          try {
            const errorBody = await errorContext.json();
            console.error('Claude error body:', errorBody);
            throw new Error(`Claude: ${errorBody.error || error.message}`);
          } catch (parseErr) {
            console.error('Failed to parse error:', parseErr);
            throw new Error(`Claude: ${error.message}`);
          }
        }
        throw new Error(`Claude: ${error.message || 'API failed'}`);
      }

      return {
        used_urls: data?.used_urls || [],
        considered: data?.considered || [],
      };
    } catch (err) {
      // Re-throw with provider prefix if not already added
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (!message.startsWith('Claude:')) {
        throw new Error(`Claude: ${message}`);
      }
      throw err;
    }
  },
});

export const PROVIDERS: Provider[] = [
  createOpenAIProvider(),
  createClaudeProvider(),
];

export const getProviderById = (id: ProviderId): Provider | undefined => {
  return PROVIDERS.find((p) => p.id === id);
};
